import { useEffect, useState } from 'react';
import type { CandidateResponseRow } from '../../db/types';
import { partyVar } from '../../lib/buildCandidates';
import { useFeatureFlags } from '../../lib/useFeatureFlags';
import Header from '../Header';
import Footer from '../Footer';
import './KnowBeforeYouVote.css';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const PARTIES = ['PL', 'PN', 'ADPD', 'Momentum', 'Aħwa Maltin', 'Imperium', 'Independent'];

export default function KnowBeforeYouVotePage() {
  const flags = useFeatureFlags();
  // The `featured_candidate` flag gates the carousel on the homepage;
  // also gate this page so flipping it 'hidden' kills the whole KBYV
  // feature from one /admin/flags toggle.
  const flagStatus = flags.featured_candidate?.status;
  const isHidden = flagStatus === 'hidden';

  const [profiles, setProfiles] = useState<CandidateResponseRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [party, setParty] = useState<string | null>(null);
  const [district, setDistrict] = useState<number | null>(null);

  useEffect(() => {
    fetch('/api/know-before-you-vote')
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d: { profiles: CandidateResponseRow[] }) => {
        setProfiles(d.profiles);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const presentParties = [...new Set(profiles.map((p) => p.party))].sort((a, b) => {
    const order = PARTIES.indexOf(a) - PARTIES.indexOf(b);
    return order !== 0 ? order : a.localeCompare(b);
  });

  const presentDistricts = [...new Set(profiles.map((p) => p.district).filter((d): d is number => d !== null))].sort(
    (a, b) => a - b,
  );

  const filtered = profiles.filter((p) => {
    if (party && p.party !== party) return false;
    if (district !== null && p.district !== district) return false;
    return true;
  });

  if (isHidden) {
    return (
      <>
        <Header />
        <div className="kbyv-page">
          <div className="kbyv-page__hero">
            <h1 className="kbyv-page__title">Not available</h1>
            <p className="kbyv-page__subtitle">
              The Know Before You Vote section is currently unavailable.{' '}
              <a href="/" style={{ textDecoration: 'underline' }}>
                Go back to the homepage.
              </a>
            </p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
    <div className="kbyv-page">
      <div className="kbyv-page__hero">
        <span className="kbyv-page__sponsored">Sponsored</span>
        <h1 className="kbyv-page__title">Know Before You Vote</h1>
        <p className="kbyv-page__subtitle">
          Candidates share their vision, priorities, and why they&apos;re asking for your vote.
        </p>
      </div>

      {(presentParties.length > 0 || presentDistricts.length > 0) && (
        <div className="kbyv-page__filters">
          <button
            type="button"
            className={`kbyv-page__filter-chip${party === null && district === null ? ' is-active' : ''}`}
            onClick={() => { setParty(null); setDistrict(null); }}
          >
            All
          </button>
          {presentParties.map((p) => (
            <button
              key={p}
              type="button"
              className={`kbyv-page__filter-chip${party === p ? ' is-active' : ''}`}
              onClick={() => setParty(party === p ? null : p)}
              style={party === p ? {} : { color: partyVar(p), borderColor: `color-mix(in srgb, ${partyVar(p)} 35%, transparent)` }}
            >
              {p}
            </button>
          ))}
          {presentDistricts.map((d) => (
            <button
              key={d}
              type="button"
              className={`kbyv-page__filter-chip${district === d ? ' is-active' : ''}`}
              onClick={() => setDistrict(district === d ? null : d)}
            >
              D{d}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="kbyv-page__grid">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="kbyv-card" style={{ opacity: 0.4, pointerEvents: 'none' }}>
              <div className="kbyv-card__body">
                <div className="kbyv-card__top">
                  <div className="kbyv-avatar kbyv-card__avatar" style={{ background: 'var(--border)' }} />
                  <div className="kbyv-card__info">
                    <div style={{ height: 14, width: '70%', background: 'var(--border)', borderRadius: 3, marginBottom: 6 }} />
                    <div style={{ height: 10, width: '40%', background: 'var(--border)', borderRadius: 3 }} />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="kbyv-page__grid">
          {filtered.length === 0 && (
            <p className="kbyv-page__empty">No candidates match the selected filters.</p>
          )}
          {filtered.map((profile) => {
            const color = partyVar(profile.party);
            const snippet = profile.why_vote ?? profile.top_priorities ?? profile.malta_vision ?? '';
            return (
              <a
                key={profile.id}
                href={`/know-before-you-vote/${profile.slug}`}
                className="kbyv-card"
                style={{ borderTopColor: color }}
              >
                <div className="kbyv-card__body">
                  <div className="kbyv-card__top">
                    <div
                      className="kbyv-avatar kbyv-card__avatar"
                      style={{ background: color }}
                      aria-hidden="true"
                    >
                      {initials(profile.candidate_name)}
                    </div>
                    <div className="kbyv-card__info">
                      <p className="kbyv-card__name">{profile.candidate_name}</p>
                      <div className="kbyv-card__meta">
                        <span className="kbyv-card__party" style={{ background: color }}>
                          {profile.party}
                        </span>
                        {profile.district && (
                          <span className="kbyv-card__district">District {profile.district}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {snippet && <p className="kbyv-card__snippet">{snippet}</p>}
                </div>
              </a>
            );
          })}
        </div>
      )}
    </div>
      <Footer />
    </>
  );
}
